import type React from 'react';
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Building2,
  FileText,
  Users,
  LogOut,
  Menu,
  ChevronLeft,
  User,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { TourHelpButton } from '@/components/ui';
import { triggerTourRestart } from '@/hooks/useTourRestart';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/constants/routes';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  tourId: string;
}

const navItems: NavItem[] = [
  { path: ROUTES.ADMIN_DASHBOARD, label: 'Дашборд', icon: <BarChart3 className="w-5 h-5" />, tourId: 'admin:nav-dashboard' },
  { path: ROUTES.ADMIN_BRANCHES, label: 'ОЦ', icon: <Building2 className="w-5 h-5" />, tourId: 'admin:nav-branches' },
  { path: ROUTES.ADMIN_APPLICATIONS, label: 'Заявки', icon: <FileText className="w-5 h-5" />, tourId: 'admin:nav-applications' },
  { path: ROUTES.ADMIN_USERS, label: 'Пользователи', icon: <Users className="w-5 h-5" />, tourId: 'admin:nav-users' },
];

const MOBILE_BREAKPOINT = 768;

const SuperAdminLayout: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        data-tour="admin:sidebar"
        className={cn(
          'fixed left-0 top-0 z-50 h-screen bg-white border-r border-gray-100 transition-all duration-300 flex flex-col',
          isMobile && !sidebarOpen && '-translate-x-full',
          isMobile && sidebarOpen && 'translate-x-0 w-64',
          !isMobile && (sidebarOpen ? 'w-64' : 'w-[72px]'),
        )}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-sm">AF</span>
            </div>
            {(sidebarOpen || isMobile) && (
              <span className="font-semibold text-gray-900 text-lg truncate">Админ</span>
            )}
          </div>
          {isMobile && sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Закрыть"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  !isMobile && !sidebarOpen && 'justify-center px-0',
                )
              }
              title={item.label}
              data-tour={item.tourId}
            >
              {item.icon}
              {(sidebarOpen || isMobile) && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {!isMobile && (
          <div className="p-3 border-t border-gray-100 shrink-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="flex items-center justify-center w-full p-2.5 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
              aria-label={sidebarOpen ? 'Свернуть' : 'Развернуть'}
            >
              <ChevronLeft className={cn('w-5 h-5 transition-transform', !sidebarOpen && 'rotate-180')} />
              {sidebarOpen && <span className="ml-2 text-sm">Свернуть</span>}
            </button>
          </div>
        )}
      </aside>

      <header
        className={cn(
          'fixed top-0 right-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-4 md:px-6 transition-all duration-300',
          isMobile ? 'left-0' : (sidebarOpen ? 'left-64' : 'left-[72px]'),
        )}
      >
        <div>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Меню"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <TourHelpButton onClick={triggerTourRestart} dataTour="admin:help-btn" />
          {user && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                <User className="w-4 h-4 text-primary-600" />
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900 leading-tight">{user.full_name}</p>
                <p className="text-xs text-gray-500">Администратор</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                aria-label="Выйти"
                title="Выйти"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      <main
        className={cn(
          'pt-16 min-h-screen transition-all duration-300',
          !isMobile && (sidebarOpen ? 'ml-64' : 'ml-[72px]'),
        )}
      >
        <div className={cn(isMobile ? 'p-4' : 'p-6')}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default SuperAdminLayout;
